/**
 * BfSaved, delat spara-lager för favoriter (Vercel-backenden).
 *
 * Knappar renderas av snippets/bf-save-buttons.liquid:
 *   [data-bf-fav-btn]     , hjärta, optimistisk toggle med id-cache i localStorage
 *
 * Auth går via window.bfUpAuth (assets/bf-upholstery.js): bearer-token i localStorage,
 * lazy token-exchange och 401-retry, ingen "vänta på token"-timeout behövs här.
 *
 * window.BfSaved exponerar api/el/dialog/toast åt konto-vyn (bf-account-hub.js).
 */

import { createDebugger } from '~/scripts/utils/debug.js';

const debug = createDebugger('bf-saved', false);

const FAV_KEY = 'bf_fav_ids';
const TOKEN_KEY = 'bf_token';
const SYNC_KEY = 'bf_fav_synced';

const backendUrl = () =>
    (document.querySelector('meta[name="bf-backend-url"]') || {}).content ||
    'https://backend-sam-8064s-projects.vercel.app';

const bootstrap = () => window.bfCustomerBootstrap || null;

// ---- token-sanering: släng token som är utgången, tillhör annan kund eller finns kvar efter utloggning ----
function decodeJwt(token) {
    try {
        return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
        return null;
    }
}

function validateToken() {
    let token = '';
    try {
        token = localStorage.getItem(TOKEN_KEY) || '';
    } catch {
        return;
    }
    const boot = bootstrap();
    if (!token) {
        if (!boot) clearCaches();
        return;
    }
    const payload = decodeJwt(token);
    const expired = !payload || (payload.exp && payload.exp * 1000 < Date.now() + 60000);
    const wrongCustomer = payload && boot && String(payload.sub) !== String(boot.customer_id);
    if (expired || wrongCustomer || !boot) {
        try {
            localStorage.removeItem(TOKEN_KEY);
        } catch {}
        if (wrongCustomer || !boot) clearCaches();
    }
}

function clearCaches() {
    try {
        localStorage.removeItem(FAV_KEY);
        sessionStorage.removeItem(SYNC_KEY);
    } catch {}
}

// ---- API ----
function api(path, options = {}) {
    const doFetch = window.bfUpAuth
        ? (u, o) => window.bfUpAuth.authFetch(u, o)
        : (u, o) => fetch(u, Object.assign({ credentials: 'include' }, o));
    return doFetch(backendUrl() + path, {
        ...options,
        headers: Object.assign({ 'Content-Type': 'application/json' }, options.headers || {})
    }).then(async (r) => {
        const t = await r.text();
        let body;
        try {
            body = JSON.parse(t);
        } catch {
            body = { raw: t };
        }
        if (!r.ok) throw Object.assign(new Error(body.error || r.statusText), { status: r.status, body });
        return body;
    });
}

// ---- favorit-cache: product_id-set i localStorage så hjärtan fylls utan nätverksanrop ----
function getFavIds() {
    try {
        return new Set(JSON.parse(localStorage.getItem(FAV_KEY) || '[]').map(String));
    } catch {
        return new Set();
    }
}

function setFavIds(set) {
    try {
        localStorage.setItem(FAV_KEY, JSON.stringify([...set]));
    } catch {}
}

function syncFavButtons(root) {
    const ids = getFavIds();
    (root || document).querySelectorAll('[data-bf-fav-btn]').forEach((btn) => {
        const on = ids.has(String(btn.getAttribute('data-product-id')));
        btn.classList.toggle('is-favorited', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
}

// En server-sync per browsersession, därefter hålls cachen uppdaterad av togglarna själva.
async function refreshFavIds(force) {
    if (!bootstrap()) return;
    try {
        if (!force && sessionStorage.getItem(SYNC_KEY)) return;
    } catch {}
    try {
        const { favorites } = await api('/api/favorites');
        setFavIds(new Set(favorites.map((f) => String(f.product_id))));
        try {
            sessionStorage.setItem(SYNC_KEY, '1');
        } catch {}
        syncFavButtons();
        document.dispatchEvent(new CustomEvent('bf:favorites-changed'));
    } catch (e) {
        debug.warn('kunde inte synka favoriter', e);
    }
}

// ---- UI-hjälpare (delas med bf-account-hub.js) ----
function el(tag, attrs = {}, children = []) {
    const node = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
        if (k === 'class') node.className = v;
        else if (k === 'html') node.innerHTML = v;
        else if (k.startsWith('on')) node.addEventListener(k.slice(2), v);
        else node.setAttribute(k, v);
    }
    for (const c of [].concat(children)) {
        if (c == null) continue;
        node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    }
    return node;
}

function dialog(title, content, actions) {
    document.querySelectorAll('.bf-modal').forEach((m) => m.remove());
    const overlay = el('div', { class: 'bf-modal' });
    const card = el('div', { class: 'bf-modal__card' });
    card.appendChild(el('h3', { class: 'bf-modal__title' }, title));
    card.appendChild(content);
    const actionBar = el('div', { class: 'bf-modal__actions' });
    for (const a of actions) actionBar.appendChild(a);
    card.appendChild(actionBar);
    overlay.appendChild(card);
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) overlay.remove();
    });
    document.body.appendChild(overlay);
    return overlay;
}

function toast(msg) {
    const t = el('div', { class: 'bf-toast' }, msg);
    document.body.appendChild(t);
    requestAnimationFrame(() => t.classList.add('bf-toast--visible'));
    setTimeout(() => {
        t.classList.remove('bf-toast--visible');
        setTimeout(() => t.remove(), 350);
    }, 3000);
}

// ---- pending action: den handling kunden försökte göra medan utloggad ----
// Sparas innan login-redirect och spelas upp direkt efter att kunden är inloggad,
// oavsett vilken sida Shopify råkar landa på. Så favoriten (eller annan gated
// handling) fullföljs av sig själv, kunden behöver inte klicka igen.
const PENDING_KEY = 'bf_pending_action';
const PENDING_MAX_AGE = 30 * 60 * 1000; // 30 min

function savePending(action) {
    try {
        localStorage.setItem(PENDING_KEY, JSON.stringify({ ...action, ts: Date.now() }));
    } catch {}
}

function readPending() {
    try {
        const a = JSON.parse(localStorage.getItem(PENDING_KEY) || 'null');
        if (!a || Date.now() - (a.ts || 0) > PENDING_MAX_AGE) {
            clearPending();
            return null;
        }
        return a;
    } catch {
        return null;
    }
}

function clearPending() {
    try {
        localStorage.removeItem(PENDING_KEY);
    } catch {}
}

// Utför favorit-sparningen direkt (används både av knappen och av replay efter login).
async function performFavorite(payload) {
    if (!payload || !payload.product_id) return;
    const id = String(payload.product_id);
    const ids = getFavIds();
    if (ids.has(id)) {
        syncFavButtons();
        return;
    }
    ids.add(id);
    setFavIds(ids);
    syncFavButtons();
    try {
        await api('/api/favorites', { method: 'POST', body: JSON.stringify(payload) });
        document.dispatchEvent(new CustomEvent('bf:favorites-changed'));
        refreshFavIds(true);
    } catch (err) {
        const revert = getFavIds();
        revert.delete(id);
        setFavIds(revert);
        syncFavButtons();
        debug.error('kunde inte spara favorit', err);
    }
}

// Kör den sparade handlingen så fort kunden är inloggad. En handling per typ.
function replayPending() {
    if (!bootstrap()) return;
    const a = readPending();
    if (!a) return;
    clearPending();
    if (a.type === 'favorite') {
        toast(window.theme.strings.bfSavedToFavorites);
        performFavorite(a.payload);
    }
    // framtida typer (save-combo, sample) registreras här
}

// pending = { type, ...data } som ska spelas upp efter login. Valfri.
function loginRedirect(pending) {
    if (pending) savePending(pending);
    if (window.bfUpAuth && window.bfUpAuth.rememberReturn) window.bfUpAuth.rememberReturn();
    // Nya kundkonton: bara /customer_authentication/login?return_to=<relativ sökväg> tar
    // kunden tillbaka hit efter login. /account/login tappar return_to (landar på kontot).
    location.href = '/customer_authentication/login?return_to=' + encodeURIComponent(location.pathname + location.search);
}

function productPayload(btn) {
    return {
        product_id: Number(btn.getAttribute('data-product-id')),
        variant_id: btn.getAttribute('data-variant-id') ? Number(btn.getAttribute('data-variant-id')) : null,
        product_handle: btn.getAttribute('data-product-handle') || null,
        product_title: btn.getAttribute('data-product-title') || null,
        product_image: btn.getAttribute('data-product-image') || null,
        product_vendor: btn.getAttribute('data-product-vendor') || null,
        product_type: btn.getAttribute('data-product-type') || null
    };
}

// ---- hjärta: optimistisk toggle, UI + cache uppdateras direkt, backas vid fel ----
function onFavClick(btn) {
    const payload = productPayload(btn);
    if (!bootstrap()) {
        if (!payload.product_id) return;
        // Utloggad: visa login-modalen (steget före inloggning) i stället för att
        // redirecta direkt. Modalens CTA går till samma login-url, och savePending
        // körs precis före navigering så favoriten spelas upp automatiskt efter login.
        if (window.bfLoginModal) {
            window.bfLoginModal.open({
                variant: 'favorite',
                onProceed: () => {
                    savePending({ type: 'favorite', payload });
                    if (window.bfUpAuth && window.bfUpAuth.rememberReturn) window.bfUpAuth.rememberReturn();
                }
            });
        } else {
            // Fallback om modalen inte laddats: gamla direktflödet.
            toast(window.theme.strings.bfLoginToSave);
            setTimeout(() => loginRedirect({ type: 'favorite', payload }), 1000);
        }
        return;
    }
    if (!payload.product_id) return;
    const id = String(payload.product_id);
    const ids = getFavIds();
    const wasFav = ids.has(id);
    if (wasFav) ids.delete(id);
    else ids.add(id);
    setFavIds(ids);
    syncFavButtons();
    // Optimistiskt: meddela lyssnare (favoritlistor) direkt, vänta inte på servern.
    document.dispatchEvent(new CustomEvent('bf:favorites-changed'));

    const req = wasFav
        ? api('/api/favorites?product_id=' + id, { method: 'DELETE' })
        : api('/api/favorites', { method: 'POST', body: JSON.stringify(payload) });
    req.catch((err) => {
        const revert = getFavIds();
        if (wasFav) revert.add(id);
        else revert.delete(id);
        setFavIds(revert);
        syncFavButtons();
        document.dispatchEvent(new CustomEvent('bf:favorites-changed')); // backa listorna vid fel
        if (err.status === 401) {
            toast(window.theme.strings.bfLoginToSave);
            setTimeout(() => loginRedirect({ type: 'favorite', payload }), 1000);
        } else {
            toast(window.theme.strings.bfSaveFailed);
            debug.error('favorit-toggle failade', err);
        }
    });
}

document.addEventListener('click', (e) => {
    const fav = e.target.closest('[data-bf-fav-btn]');
    if (fav) {
        e.preventDefault();
        e.stopPropagation();
        onFavClick(fav);
    }
});

function init() {
    validateToken();
    syncFavButtons();
    refreshFavIds();
    replayPending();
    // Temat AJAX-renderar produktgrids (filter, pagination, quick shop), synka nytillkomna hjärtan
    if (window.MutationObserver) {
        let scheduled = false;
        new MutationObserver(() => {
            if (scheduled) return;
            scheduled = true;
            setTimeout(() => {
                scheduled = false;
                syncFavButtons();
            }, 100);
        }).observe(document.body, { childList: true, subtree: true });
    }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

window.BfSaved = {
    api,
    el,
    dialog,
    toast,
    loginRedirect,
    getFavIds,
    setFavIds,
    syncFavButtons,
    refreshFavIds
};
